import { Injectable } from '@angular/core';
import {
    FlightLogCriteria,
    FlightLogsService,
    FlightLogSummaryGQL,
    FlyFreelyError,
    FlyFreelyLoggingService,
    SimplePersonDto,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { ColumnSortPreferences } from '@flyfreely-portal-ui/flyfreely-table';
import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

const defaultSort: { field: string; order: 'ASC' | 'DESC' } = {
    field: 'startTime',
    order: 'DESC'
};

export interface OutstandingFlightLogsFlight {
    id: number;
    missionName: string;
    missionId: number;
    flightLogName: string;
    rpaName: string;
    rpaSerialNumber: string;
    pilotName?: SimplePersonDto;
    startTime: string;
    conformanceResultStatus: string;
}

@Injectable()
export class OutstandingFlightLogsDataService {
    private workingSource = new ReplaySubject<boolean>(1);
    private flightLogsSource = new ReplaySubject<OutstandingFlightLogsFlight[]>(
        1
    );
    private currentPageSource = new BehaviorSubject<number>(0);
    private totalItemsSource = new ReplaySubject<number>(1);
    private hasFlightLogsSource = new ReplaySubject<boolean>(1);

    working$ = this.workingSource.asObservable();
    flightLogs$ = this.flightLogsSource.asObservable();
    currentPage$ = this.currentPageSource.asObservable();
    totalItems$ = this.totalItemsSource.asObservable();
    hasFlightLogs$ = this.hasFlightLogsSource.asObservable();

    private workTracker = new WorkTracker();
    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private flightLogsService: FlightLogsService,
        private logging: FlyFreelyLoggingService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => this.workingSource.next(working));
    }

    ngOnDestroy() {
        this.workingSource.complete();
        this.flightLogsSource.complete();
        this.currentPageSource.complete();
        this.totalItemsSource.complete();
        this.hasFlightLogsSource.complete();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    findFlightLogs(
        page: number,
        limit: number,
        sorting: ColumnSortPreferences,
        filters: FlightLogCriteria
    ) {
        // doneWorking is used for GraphQL to prevent the working state ending early.
        const sortFields: { field: string; order: 'ASC' | 'DESC' } =
            sorting != null
                ? {
                      field: sorting?.column ?? null,
                      order: sorting?.ascending ? 'ASC' : 'DESC'
                  }
                : defaultSort;
        this.flightLogsService
            .findFlightLogsSummaries(filters, sortFields, page, limit)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: logs => {
                    const flightLogs: OutstandingFlightLogsFlight[] =
                        this.parseFlightLogData(logs.results, page, limit);
                    this.totalItemsSource.next(logs.count);
                    this.currentPageSource.next(page);
                    this.flightLogsSource.next(flightLogs);
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(error);
                }
            })
            .add(this.workTracker.createTracker());
    }

    parseFlightLogData(
        flightLogs: FlightLogSummaryGQL[],
        page: number,
        limit: number
    ): OutstandingFlightLogsFlight[] {
        const parseConformanceResults = (
            conformanceResult: FlightLogSummaryGQL['conformanceResult']
        ) => {
            if (!conformanceResult) {
                return null;
            }
            const breachType =
                conformanceResult.ruleResultList?.[0]?.ruleVersion?.rule?.type;
            return breachType != null ? conformanceResult.status : null;
        };
        return flightLogs.map(log => ({
            id: log.id,
            missionName: log.mission?.name,
            missionId: log.mission?.id,
            flightLogName: log.fileName,
            rpaName: log.rpa?.nickname,
            rpaSerialNumber: log.summary?.rpaSerialNumber,
            pilotName: log.flight?.pilot,
            startTime: log.summary?.startTime,
            conformanceResultStatus: parseConformanceResults(
                log.conformanceResult
            )
        }));
    }
}
